import { assert, cx, useTranslations } from "@jugl-web/utils";
import { ReactNode, useMemo, useRef, useState } from "react";
import { Button } from "../../cross-platform/Button";
import { InlineAlert } from "../../cross-platform/InlineAlert";
import {
  ListBox,
  ListBoxHandle,
  ListBoxItem,
  ListBoxProps,
  UnsafeListBoxItem,
} from "../../cross-platform/ListBox";
import { Popover, PopoverProps } from "../../cross-platform/Popover";
import { ResourcePickerPopoverHeader } from "./ResourcePickerPopoverHeader";

interface EnhancedListBoxProps<TValue>
  extends Omit<ListBoxProps<TValue>, "onSelect" | "className"> {
  onSelect?: (props: {
    item: ListBoxItem<TValue>;
    isSelected: boolean;
    onClose: () => void;
  }) => void;
}

type SupportedPopoverProps = Pick<
  PopoverProps,
  | "autoPosition"
  | "backdropStyle"
  | "hasBackdrop"
  | "isDisabled"
  | "onUnmount"
  | "placement"
  | "adjustToTriggerWidth"
  | "renderTrigger"
>;

interface CustomButtonLabelProps {
  selectedItemsCount: number;
  defaultButtonLabel: string;
}

export interface SelectionCountConstraint {
  value: number;
  errorMessage?: string | ((selectionCount: number) => string);
}

export interface SelectionConstraints {
  minCount?: SelectionCountConstraint;
  maxCount?: SelectionCountConstraint;
}

export interface ResourcePickerPopoverProps<TValue>
  extends SupportedPopoverProps,
    EnhancedListBoxProps<TValue> {
  title?: ReactNode;
  subtitle?: ReactNode;
  className?: string;
  customButtonLabel?: string | ((props: CustomButtonLabelProps) => string);
  customFooterElement?: ReactNode;
  selectionConstraints?: SelectionConstraints;
  onOpenStateChange?: (isOpen: boolean) => void;
  onSubmit?: (ids: string[], items: UnsafeListBoxItem<TValue>[]) => void;
}

export const ResourcePickerPopover = <TValue,>({
  title,
  subtitle,
  placement,
  autoPosition,
  hasBackdrop,
  backdropStyle,
  isDisabled,
  customButtonLabel,
  className,
  customFooterElement,
  selectionConstraints,
  adjustToTriggerWidth,
  renderTrigger,
  onSelect,
  onSelectionChange,
  onOpenStateChange,
  onSubmit,
  onUnmount,
  ...listBoxProps
}: ResourcePickerPopoverProps<TValue>) => {
  const [selectedItemsCount, setSelectedItemsCount] = useState(0);
  const listBoxRef = useRef<ListBoxHandle<TValue> | null>(null);

  const { t } = useTranslations();

  const hasHeader = !!title;
  const hasSubmitButton =
    !!onSubmit && listBoxProps.selectionBehavior.mode !== "readonly";

  const isMultipleSelection =
    listBoxProps.selectionBehavior.mode === "multiple";

  const shouldShowSelectedItemsCounter =
    isMultipleSelection && selectedItemsCount > 0;

  const defaultButtonLabel = useMemo(() => {
    if (shouldShowSelectedItemsCounter) {
      return t(
        {
          id: "common.select-with-count",
          defaultMessage: "Select {count}",
        },
        {
          count: selectedItemsCount,
        }
      );
    }

    return t({
      id: "common.select",
      defaultMessage: "Select",
    });
  }, [selectedItemsCount, shouldShowSelectedItemsCounter, t]);

  const buttonLabel = useMemo(() => {
    if (customButtonLabel) {
      if (typeof customButtonLabel === "function") {
        return customButtonLabel({ selectedItemsCount, defaultButtonLabel });
      }

      return customButtonLabel;
    }

    return defaultButtonLabel;
  }, [customButtonLabel, selectedItemsCount, defaultButtonLabel]);

  const validationError = useMemo(() => {
    if (!selectionConstraints) {
      return null;
    }

    const { minCount, maxCount } = selectionConstraints;

    // Check minimum count constraint
    if (
      minCount &&
      selectedItemsCount < minCount.value &&
      minCount.errorMessage
    ) {
      return typeof minCount.errorMessage === "function"
        ? minCount.errorMessage(selectedItemsCount)
        : minCount.errorMessage;
    }

    // Check maximum count constraint
    if (
      maxCount &&
      selectedItemsCount > maxCount.value &&
      maxCount.errorMessage
    ) {
      return typeof maxCount.errorMessage === "function"
        ? maxCount.errorMessage(selectedItemsCount)
        : maxCount.errorMessage;
    }

    return null;
  }, [selectionConstraints, selectedItemsCount]);

  const isSubmitDisabled = useMemo(() => {
    if (!selectionConstraints) {
      return false;
    }

    const { minCount, maxCount } = selectionConstraints;

    // Disable if below minimum count
    if (minCount && selectedItemsCount < minCount.value) {
      return true;
    }

    // Disable if above maximum count
    if (maxCount && selectedItemsCount > maxCount.value) {
      return true;
    }

    return false;
  }, [selectionConstraints, selectedItemsCount]);

  const handlePopoverMount = () => {
    if (listBoxRef.current) {
      setSelectedItemsCount(listBoxRef.current.getSelectedIds().length);
    }
    onOpenStateChange?.(true);
  };

  const handlePopoverUnmount = () => {
    setSelectedItemsCount(0);
    onUnmount?.();
    onOpenStateChange?.(false);
  };

  return (
    <Popover
      renderTrigger={renderTrigger}
      placement={placement}
      autoPosition={autoPosition}
      hasBackdrop={hasBackdrop}
      backdropStyle={backdropStyle}
      adjustToTriggerWidth={adjustToTriggerWidth}
      isDisabled={isDisabled}
      className={cx("z-[2000] w-[315px]", className)}
      onMount={handlePopoverMount}
      onUnmount={handlePopoverUnmount}
    >
      {({ onClose }) => (
        <>
          {hasHeader && (
            <ResourcePickerPopoverHeader
              title={title}
              subtitle={subtitle}
              onClose={onClose}
            />
          )}
          <div className="p-3">
            <ListBox
              ref={listBoxRef}
              isCompact
              buttonType={isMultipleSelection ? "checkbox" : "checkmark"}
              onSelectionChange={(ids, items) => {
                onSelectionChange?.(ids, items);
                setSelectedItemsCount(ids.length);
              }}
              onSelect={
                onSelect
                  ? (item, isSelected) =>
                      onSelect({ item, isSelected, onClose })
                  : undefined
              }
              {...listBoxProps}
              extraTopContent={
                validationError ? (
                  <InlineAlert variant="warning">{validationError}</InlineAlert>
                ) : undefined
              }
              addButton={
                listBoxProps.addButton && {
                  ...listBoxProps.addButton,
                  onClick: () => {
                    listBoxProps.addButton?.onClick();
                    onClose();
                  },
                }
              }
            />
            {hasSubmitButton && (
              <div className="pb-1 pt-4">
                <div>{customFooterElement}</div>
                <div className="grid grid-cols-2 gap-4">
                  <Button
                    variant="contained"
                    color="grey"
                    fullWidth
                    className="font-secondary h-10"
                    onClick={() => {
                      onClose();
                    }}
                  >
                    {t({
                      id: "common.cancel",
                      defaultMessage: "Cancel",
                    })}
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    className="font-secondary h-10"
                    isDisabled={isSubmitDisabled}
                    onClick={() => {
                      assert(!!listBoxRef.current, "ListBox ref is not set");

                      onSubmit(
                        listBoxRef.current.getSelectedIds(),
                        listBoxRef.current.getSelectedItems()
                      );

                      onClose();
                    }}
                  >
                    {buttonLabel}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </Popover>
  );
};
